import { ShopFilters } from "queries/shops";
import { Api } from "queries/types";
import { useState } from "react";

export enum UpdatedAtFilterOption {
  M1 = "m1",
  M1M3 = "m1m3",
  M3 = "m3",
}

export enum completionRateFilterOption {
  T100 = "100",
  T50 = "s50",
  T0 = "s0",
}

export interface FiltersState {
  brandId: number | null;
  updatedAt: UpdatedAtFilterOption | null;
  sectorManagerId: number | null;
  searchTerm: string | null;
  completionRate: completionRateFilterOption | null;
}

const toDayString = (daysAgo: number) => {
  const today = new Date();
  var date = new Date();
  date.setDate(today.getDate() - daysAgo);

  return date.toISOString().split("T")[0];
};

const toParams = (filters: FiltersState): ShopFilters => {
  const oneMonthAgo = toDayString(30);
  const threeMonthsAgo = toDayString(90);

  const updatedAtOptions = {
    [UpdatedAtFilterOption.M1]: { "updatedAt[after]": oneMonthAgo },
    [UpdatedAtFilterOption.M1M3]: { "updatedAt[after]": threeMonthsAgo, "updatedAt[before]": oneMonthAgo },
    [UpdatedAtFilterOption.M3]: { "updatedAt[before]": threeMonthsAgo },
  };

  const updatedAtParams = filters.updatedAt ? updatedAtOptions[filters.updatedAt] : {};

  return { sectorManagerId: filters.sectorManagerId, brandId: filters.brandId, ...updatedAtParams };
};

export function filteredShop(shops: Api.Shop[], filtersParams: ShopFilters, searchTerm:string, completionRate: completionRateFilterOption | null) {
  let shopsFiltered = shops;

  shopsFiltered = searchTerm === ""
    ? shops
    : shops.filter((shop) =>
        shop.name.toLowerCase().match(searchTerm.toLowerCase()) ||
        shop.shortName.toLowerCase().match(searchTerm.toLowerCase()) ||
        shop.sectorManagerFullName.toLowerCase().match(searchTerm.toLowerCase()) ||
        shop.brandName.toLowerCase().match(searchTerm.toLowerCase()) ||
        shop.country.toLowerCase().match(searchTerm.toLowerCase())
      );

  if (filtersParams.sectorManagerId) {
    shopsFiltered = shopsFiltered.filter((shop) => shop.sectorManagerId === filtersParams.sectorManagerId);
  }

  if (filtersParams.brandId) {
    shopsFiltered = shopsFiltered.filter((shop) => shop.brandId === filtersParams.brandId);
  }

  if (filtersParams["updatedAt[after]"]) {
    shopsFiltered = shopsFiltered.filter((shop) => {
      if (!shop.activeGridUpdatedAt) return false;
      const activeGridUpdatedAt = new Date(shop.activeGridUpdatedAt);
      return activeGridUpdatedAt.getTime() - new Date(filtersParams["updatedAt[after]"]!).getTime() > 0;
    });
  }

  if (filtersParams["updatedAt[before]"]) {
    shopsFiltered = shopsFiltered.filter((shop) => {
      if (!shop.activeGridUpdatedAt) return false;
      const activeGridUpdatedAt = new Date(shop.activeGridUpdatedAt);
      return new Date(filtersParams["updatedAt[before]"]!).getTime() - activeGridUpdatedAt.getTime() > 0;
    });
  }

  if (completionRate) {
    shopsFiltered = shopsFiltered.filter((shop) => {
      let isCompletionRateInRange = false;
      switch (completionRate) {
        case completionRateFilterOption.T100:
          isCompletionRateInRange = shop.completionRate === 1;
          break;
        case completionRateFilterOption.T50:
          isCompletionRateInRange = shop.completionRate < 1 && shop.completionRate >= 0.5;
          break;
        case completionRateFilterOption.T0:
          isCompletionRateInRange = shop.completionRate >= 0 && shop.completionRate < 0.5;
          break;
      }

      return isCompletionRateInRange;
    });
  }

  return shopsFiltered;
}

const useFilters = () => {
  const [filters, setFilters] = useState<FiltersState>({ brandId: null, sectorManagerId: null, updatedAt: null, searchTerm: null, completionRate: null });

  return { filters, setFilters, params: toParams(filters), searchTerm: filters.searchTerm || '', completionRate: filters.completionRate };
};

export default useFilters;
